/**
 * Project Service
 * Uses HTTP API when enabled, otherwise falls back to localStorage
 */

import axios from 'axios';

const PROJECTS_KEY = 'eqaya_projects';
const PROJECT_COUNTER_KEY = 'eqaya_project_counter';
const USE_API = process.env.REACT_APP_USE_API !== 'false';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Initialize project counter
const getProjectCounter = () => {
  const counter = localStorage.getItem(PROJECT_COUNTER_KEY);
  return counter ? parseInt(counter) : 1000;
};

const setProjectCounter = (counter) => {
  localStorage.setItem(PROJECT_COUNTER_KEY, counter.toString());
};

// Utility functions
const getProjects = () => {
  const projects = localStorage.getItem(PROJECTS_KEY);
  return projects ? JSON.parse(projects) : [];
};

const saveProjects = (projects) => {
  localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
};

const generateProjectId = () => {
  const counter = getProjectCounter();
  setProjectCounter(counter + 1);
  return `PROJ-${counter}`;
};

// Simulate network delay
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Project service
const projectService = {
  // Get all projects for current user
  getProjects: async (userId) => {
    if (USE_API) {
      const { data } = await apiClient.get('/projects', { params: { userId } });
      // Accept a variety of backend shapes
      // 1) { success, data: { projects: [...] } }
      // 2) { projects: [...] }
      // 3) [ ... ]
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.data?.projects)) return data.data.projects;
      if (Array.isArray(data?.projects)) return data.projects;
      return [];
    }
    const allProjects = getProjects();
    return allProjects.filter(project => project.userId === userId);
  },

  // Get single project by ID
  getProject: async (projectId) => {
    if (USE_API) {
      const { data } = await apiClient.get(`/projects/${projectId}`);
      return data.data || data;
    }
    const projects = getProjects();
    const project = projects.find(p => p.id === projectId);
    if (!project) throw new Error('Project not found');
    return project;
  },

  // Create new project
  createProject: async (userId, projectData) => {
    // Basic client-side validation retained
    const requiredFields = ['title', 'location'];
    for (let field of requiredFields) {
      if (!projectData[field] || projectData[field].toString().trim() === '') {
        throw new Error(`${field} is required.`);
      }
    }
    // Budget is optional - allow 0 for "not sure" case
    let budget = 0;
    if (projectData.budget !== undefined && projectData.budget !== null && projectData.budget !== '') {
      const parsedBudget = parseFloat(projectData.budget);
      if (isNaN(parsedBudget) || parsedBudget < 0) {
        throw new Error('Budget must be a valid positive number.');
      }
      budget = parsedBudget;
    }

    if (USE_API) {
      // Handle location - could be string or object
      let locationString = projectData.location;
      if (typeof locationString === 'object' && locationString !== null) {
        locationString = locationString.address || locationString.name || JSON.stringify(locationString);
      }
      
      const payload = {
        title: projectData.title.trim(),
        description: projectData.description?.trim() || '',
        location: locationString?.trim() || 'Not specified',
        budget,
        currency: projectData.currency || 'USD',
        project_type: projectData.project_type || projectData.category || 'other',
        task_type: projectData.task_type || null,
        full_project_type: projectData.full_project_type || null,
        // AI insights the client generated in the wizard (budget estimate, photo
        // analysis, timeline). Without this they're silently dropped here and the
        // contractor's "AI Project Insights" panel never appears.
        ai_metadata:
          projectData.ai_metadata && typeof projectData.ai_metadata === 'object'
            ? projectData.ai_metadata
            : undefined,
        // Structured wizard answers (build stage, plans, rooms, finishes,
        // materials, measurements, timeline, priorities) — rendered to
        // contractors as a spec sheet next to the prose brief.
        structured_brief:
          projectData.structured_brief && typeof projectData.structured_brief === 'object'
            ? projectData.structured_brief
            : undefined,
        manual_contractor_name: projectData.manual_contractor_name || projectData.contractor?.trim() || null,
        covers_travel: projectData.covers_travel === true,
        travel_notes:
          typeof projectData.travel_notes === 'string' && projectData.travel_notes.trim()
            ? projectData.travel_notes.trim()
            : undefined
      };
      const { data } = await apiClient.post('/projects', payload);
      return data;
    }

    const projects = getProjects();
    const newProject = {
      id: generateProjectId(),
      userId,
      title: projectData.title.trim(),
      description: projectData.description?.trim() || '',
      location: projectData.location.trim(),
      budget,
      currency: projectData.currency || 'USD',
      contractor: projectData.contractor?.trim() || '',
      status: 'planning',
      progress: 0,
      spent: 0,
      startDate: projectData.startDate || null,
      estimatedCompletion: projectData.estimatedCompletion || null,
      actualCompletion: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      milestones: [],
      payments: [],
      documents: [],
      notes: []
    };
    projects.push(newProject);
    saveProjects(projects);
    return { success: true, data: newProject, message: 'Project created successfully!' };
  },

  // Update existing project
  updateProject: async (projectId, updateData) => {
    if (USE_API) {
      const { data } = await apiClient.put(`/projects/${projectId}`, updateData);
      return data;
    }
    const projects = getProjects();
    const projectIndex = projects.findIndex(p => p.id === projectId);
    if (projectIndex === -1) throw new Error('Project not found.');
    projects[projectIndex] = { ...projects[projectIndex], ...updateData, updatedAt: new Date().toISOString() };
    saveProjects(projects);
    return { success: true, data: projects[projectIndex], message: 'Project updated successfully!' };
  },

  requestMaterialsSchedule: async (projectId) => {
    if (USE_API) {
      const { data } = await apiClient.post(`/projects/${projectId}/boq/request`);
      return data;
    }
    return { success: true, message: 'Materials schedule request sent to the assigned contractor' };
  },

  // Update project status
  updateProjectStatus: async (projectId, newStatus) => {
    if (USE_API) {
      const { data } = await apiClient.patch(`/projects/${projectId}/status`, { status: newStatus });
      return data;
    }
    const projects = getProjects();
    const projectIndex = projects.findIndex(p => p.id === projectId);
    if (projectIndex === -1) throw new Error('Project not found.');
    
    // Only update if transitioning to a valid status
    const validStatuses = ['planning', 'open_for_bids', 'in_progress', 'completed', 'cancelled'];
    if (!validStatuses.includes(newStatus)) {
      throw new Error('Invalid status.');
    }
    
    projects[projectIndex].status = newStatus;
    projects[projectIndex].updatedAt = new Date().toISOString();
    
    // Set start date if moving to in_progress
    if (newStatus === 'in_progress' && !projects[projectIndex].startDate) {
      projects[projectIndex].startDate = new Date().toISOString();
    }
    
    saveProjects(projects);
    return { success: true, data: projects[projectIndex], message: `Project status updated to ${newStatus}` };
  },

  // Delete project
  deleteProject: async (projectId) => {
    if (USE_API) {
      const { data } = await apiClient.delete(`/projects/${projectId}`);
      return data;
    }
    const projects = getProjects();
    const filteredProjects = projects.filter(p => p.id !== projectId);
    if (filteredProjects.length === projects.length) throw new Error('Project not found.');
    saveProjects(filteredProjects);
    return { success: true, message: 'Project deleted successfully!' };
  },

  // Get project statistics for user
  getProjectStats: async (userId) => {
    await delay(200);
    
    const projects = getProjects().filter(p => p.userId === userId);
    
    const stats = {
      totalProjects: projects.length,
      completedProjects: projects.filter(p => p.status === 'completed').length,
      inProgressProjects: projects.filter(p => p.status === 'in_progress').length,
      planningProjects: projects.filter(p => p.status === 'planning').length,
      totalBudget: projects.reduce((sum, p) => sum + p.budget, 0),
      totalSpent: projects.reduce((sum, p) => sum + p.spent, 0),
      averageProgress: projects.length > 0 
        ? Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length)
        : 0
    };

    return stats;
  },

  // Complete project and trigger review process
  completeProject: async (projectId, completionData = {}) => {
    await delay();
    
    const projects = getProjects();
    const projectIndex = projects.findIndex(p => p.id === projectId);
    
    if (projectIndex === -1) {
      throw new Error('Project not found.');
    }

    const project = projects[projectIndex];
    
    // Update project to completed status
    projects[projectIndex] = {
      ...project,
      status: 'completed',
      progress: 100,
      actualCompletion: new Date().toISOString(),
      completionNotes: completionData.notes || '',
      updatedAt: new Date().toISOString(),
      reviewEligible: true // Mark as eligible for review
    };

    saveProjects(projects);

    return {
      success: true,
      data: projects[projectIndex],
      message: 'Project completed successfully!',
      reviewEligible: true,
      contractorData: completionData.contractorData || null
    };
  },

  // Mark project as reviewed
  markProjectReviewed: async (projectId) => {
    if (USE_API) {
      try {
        await apiClient.patch(`/projects/${projectId}/mark-reviewed`);
        return { success: true };
      } catch (err) {
        console.warn('markProjectReviewed API failed, silently ignoring:', err.message);
        return { success: true };
      }
    }
    await delay(200);
    const projects = getProjects();
    const projectIndex = projects.findIndex(p => p.id === projectId);
    if (projectIndex !== -1) {
      projects[projectIndex] = { ...projects[projectIndex], reviewSubmitted: true, updatedAt: new Date().toISOString() };
      saveProjects(projects);
    }
    return { success: true };
  },

  // Get projects eligible for review
  getProjectsEligibleForReview: async (userId) => {
    if (USE_API) {
      try {
        const response = await apiClient.get('/projects/eligible-for-review');
        return response.data?.data || [];
      } catch (err) {
        console.warn('getProjectsEligibleForReview failed:', err.message);
        return [];
      }
    }
    await delay(300);
    return getProjects()
      .filter(p => p.userId === userId)
      .filter(p => p.status === 'completed' && p.reviewEligible && !p.reviewSubmitted);
  },

  // Create verification request for milestone
  createMilestoneVerificationRequest: async (projectId, milestoneId, requestData) => {
    await delay(500);
    
    const project = await projectService.getProject(projectId);
    if (!project) {
      throw new Error('Project not found');
    }
    
    // Import verification services (would be imported at top in real app)
    const verificationRequestService = (await import('./verificationRequestService')).default;
    const milestoneService = (await import('./milestoneService')).default;
    
    // Get milestone details
    const milestone = await milestoneService.getMilestone(milestoneId);
    if (!milestone) {
      throw new Error('Milestone not found');
    }
    
    // Create verification request
    const verificationRequest = await verificationRequestService.createVerificationRequest({
      projectId: projectId,
      milestoneId: milestoneId,
      contractorId: requestData.contractorId,
      clientId: project.userId,
      requestDetails: {
        title: `${milestone.milestoneDetails.name} Verification`,
        description: milestone.milestoneDetails.description,
        milestoneType: milestone.milestoneDetails.type,
        verificationScope: milestone.requirements.verificationScope,
        urgency: requestData.urgency || 'normal',
        estimatedDuration: requestData.estimatedDuration || 120,
        requiredEvidence: ['photos', 'videos', 'checklist']
      },
      location: requestData.location,
      scheduling: requestData.scheduling,
      payment: {
        verificationFee: requestData.verificationFee || 75.00,
        currency: 'USD'
      },
      tags: [milestone.milestoneDetails.type, project.location.split(',')[0].toLowerCase()]
    });
    
    // Link verification request to milestone
    await milestoneService.startMilestoneVerification(milestoneId, verificationRequest.data.id);
    
    return {
      success: true,
      data: {
        verificationRequest: verificationRequest.data,
        milestone: milestone
      },
      message: 'Milestone verification request created successfully!'
    };
  },

  // Get project with milestone verification status
  getProjectWithVerificationStatus: async (projectId) => {
    await delay(400);
    
    const project = await projectService.getProject(projectId);
    const milestoneService = (await import('./milestoneService')).default;
    const verificationRequestService = (await import('./verificationRequestService')).default;
    
    // Get project milestones
    const milestones = await milestoneService.getProjectMilestones(projectId);
    const progressSummary = await milestoneService.getProjectProgress(projectId);
    
    // Get verification requests for this project
    const verificationRequests = await verificationRequestService.getVerificationRequests({
      projectId: projectId
    });
    
    return {
      ...project,
      milestones: milestones,
      progressSummary: progressSummary,
      verificationRequests: verificationRequests,
      verificationStatus: {
        totalMilestones: progressSummary.totalMilestones,
        verifiedMilestones: progressSummary.verifiedMilestones,
        pendingVerifications: progressSummary.pendingVerifications,
        overallVerificationScore: verificationRequests
          .filter(vr => vr.verification.verificationScore)
          .reduce((sum, vr) => sum + vr.verification.verificationScore, 0) / 
          Math.max(1, verificationRequests.filter(vr => vr.verification.verificationScore).length) || 0
      }
    };
  },

  // Initialize project with milestones
  initializeProjectMilestones: async (projectId, projectType = 'residential', customizations = {}) => {
    await delay(600);
    
    const milestoneService = (await import('./milestoneService')).default;
    
    // Create milestones from template
    const result = await milestoneService.createMilestonesFromTemplate(
      projectId, 
      projectType, 
      customizations
    );
    
    // Update project with milestone information
    const projects = getProjects();
    const projectIndex = projects.findIndex(p => p.id === projectId);
    
    if (projectIndex !== -1) {
      projects[projectIndex].milestoneCount = result.data.length;
      projects[projectIndex].projectType = projectType;
      projects[projectIndex].milestonesInitialized = true;
      projects[projectIndex].updatedAt = new Date().toISOString();
      saveProjects(projects);
    }
    
    return result;
  },

  // Update project progress based on milestones
  updateProjectProgressFromMilestones: async (projectId) => {
    await delay(300);
    
    const milestoneService = (await import('./milestoneService')).default;
    const progressSummary = await milestoneService.getProjectProgress(projectId);
    
    // Update project progress
    const projects = getProjects();
    const projectIndex = projects.findIndex(p => p.id === projectId);
    
    if (projectIndex !== -1) {
      projects[projectIndex].progress = progressSummary.weightedProgress;
      projects[projectIndex].milestonesCompleted = progressSummary.completedMilestones;
      projects[projectIndex].milestonesTotal = progressSummary.totalMilestones;
      
      // Update project status based on milestone completion
      if (progressSummary.overallProgress === 100 && progressSummary.verifiedMilestones === progressSummary.totalMilestones) {
        projects[projectIndex].status = 'completed';
        if (!projects[projectIndex].actualCompletion) {
          projects[projectIndex].actualCompletion = new Date().toISOString();
        }
      } else if (progressSummary.overallProgress > 0) {
        projects[projectIndex].status = 'in_progress';
      }
      
      projects[projectIndex].updatedAt = new Date().toISOString();
      saveProjects(projects);
    }
    
    return {
      success: true,
      data: progressSummary,
      message: 'Project progress updated from milestones'
    };
  },

  // Get projects requiring verification
  getProjectsRequiringVerification: async (userId, userRole = 'client') => {
    await delay(400);
    
    const milestoneService = (await import('./milestoneService')).default;
    const verificationRequestService = (await import('./verificationRequestService')).default;
    
    let projects = getProjects();
    
    // Filter based on user role
    if (userRole === 'client') {
      projects = projects.filter(p => p.userId === userId);
    } else if (userRole === 'contractor') {
      projects = projects.filter(p => p.contractorId === userId);
    }
    
    const projectsWithVerification = [];
    
    for (let project of projects) {
      // Get milestones requiring verification
      const milestones = await milestoneService.getMilestonesRequiringVerification({
        projectId: project.id
      });
      
      if (milestones.length > 0) {
        // Get related verification requests
        const verificationRequests = await verificationRequestService.getVerificationRequests({
          projectId: project.id,
          status: ['pending', 'assigned', 'in_progress']
        });
        
        projectsWithVerification.push({
          ...project,
          milestonesRequiringVerification: milestones,
          pendingVerificationRequests: verificationRequests
        });
      }
    }
    
    return projectsWithVerification;
  },

  // Search projects
  searchProjects: async (userId, searchTerm, filters = {}) => {
    await delay(300);
    
    let projects = getProjects().filter(p => p.userId === userId);
    
    // Apply text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      projects = projects.filter(p => 
        p.title.toLowerCase().includes(term) ||
        p.description.toLowerCase().includes(term) ||
        p.location.toLowerCase().includes(term) ||
        p.contractor.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.status && filters.status !== 'all') {
      projects = projects.filter(p => p.status === filters.status);
    }

    if (filters.location) {
      projects = projects.filter(p => 
        p.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Sort projects
    projects.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

    return projects;
  },

  // Get user's projects (alias for getProjects with success wrapper)
  getUserProjects: async (userId) => {
    const projects = await projectService.getProjects(userId);
    return { success: true, data: projects };
  },

  // Invite contractor to project
  inviteContractor: async (projectId, contractorId) => {
    if (USE_API) {
      const { data } = await apiClient.post(`/projects/${projectId}/invite`, { contractorId });
      return data;
    }
    // Local storage fallback
    const projects = getProjects();
    const projectIndex = projects.findIndex(p => p.id === projectId);
    if (projectIndex === -1) throw new Error('Project not found');
    
    projects[projectIndex].invitedContractors = projects[projectIndex].invitedContractors || [];
    if (!projects[projectIndex].invitedContractors.includes(contractorId)) {
      projects[projectIndex].invitedContractors.push(contractorId);
    }
    projects[projectIndex].updatedAt = new Date().toISOString();
    saveProjects(projects);
    return { success: true };
  },

  // Assign contractor to project
  assignContractor: async (projectId, contractorId) => {
    if (USE_API) {
      const { data } = await apiClient.post(`/projects/${projectId}/assign`, { contractorId });
      return data;
    }
    // Local storage fallback
    const projects = getProjects();
    const projectIndex = projects.findIndex(p => p.id === projectId);
    if (projectIndex === -1) throw new Error('Project not found');
    
    projects[projectIndex].contractor_id = contractorId;
    projects[projectIndex].status = 'assigned';
    projects[projectIndex].updatedAt = new Date().toISOString();
    saveProjects(projects);
    return { success: true };
  },

  // Invite contractor by email
  inviteContractorByEmail: async (projectId, { email, name, phone, message }) => {
    if (USE_API) {
      const { data } = await apiClient.post('/contractors/invite', {
        email,
        name: name || email.split('@')[0],
        phone,
        message,
        projectId,
      });
      return data;
    }
    // Local storage fallback - just simulate success
    console.log(`Invitation email would be sent to ${email} for project ${projectId}`);
    return { success: true, message: 'Invitation sent' };
  },

  // Get available projects (for contractors to browse)
  getAvailableProjects: async () => {
    if (USE_API) {
      const { data } = await apiClient.get('/projects/available');
      return data;
    }
    // Local storage fallback
    const projects = getProjects();
    return {
      success: true,
      data: projects.filter(p => p.status === 'open' || p.status === 'pending')
    };
  },

  // Get active projects (for contractors)
  getActiveProjects: async () => {
    if (USE_API) {
      const { data } = await apiClient.get('/projects/active');
      return data;
    }
    // Local storage fallback
    const projects = getProjects();
    return {
      success: true,
      data: projects.filter(p => p.status === 'in_progress' || p.status === 'active')
    };
  },

  // Get completed projects
  getCompletedProjects: async () => {
    if (USE_API) {
      const { data } = await apiClient.get('/projects/completed');
      return data;
    }
    // Local storage fallback
    const projects = getProjects();
    return {
      success: true,
      data: projects.filter(p => p.status === 'completed')
    };
  },

  // ============================================================================
  // DAILY DIARY METHODS
  // ============================================================================

  /**
   * Get diary entries for a project
   * @param {string|number} projectId - Project ID
   * @param {Object} options - Query options (limit, offset, startDate, endDate, milestoneId)
   * @returns {Promise<Object>} Entries and pagination metadata
   */
  getDiaryEntries: async (projectId, options = {}) => {
    const { limit = 20, offset = 0, startDate, endDate, milestoneId } = options;
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (milestoneId) params.append('milestoneId', milestoneId.toString());

    if (USE_API) {
      const { data } = await apiClient.get(`/projects/${projectId}/diary?${params}`);
      const entries = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      const pagination = data?.pagination || { total: 0, limit, offset, hasMore: false };
      return { entries, pagination };
    }

    // Local storage fallback
    return { entries: [], pagination: { total: 0, limit, offset, hasMore: false } };
  },

  /**
   * Get a single diary entry
   * @param {string|number} projectId - Project ID
   * @param {string|number} entryId - Entry ID
   * @returns {Promise<Object>} Diary entry
   */
  getDiaryEntry: async (projectId, entryId) => {
    if (USE_API) {
      const { data } = await apiClient.get(`/projects/${projectId}/diary/${entryId}`);
      return data.data || data;
    }

    // Local storage fallback
    throw new Error('Entry not found');
  },

  /**
   * Create a new diary entry
   * @param {string|number} projectId - Project ID
   * @param {Object} entryData - Entry data (entryDate, description, milestoneId, photoUrls)
   * @returns {Promise<Object>} Created entry
   */
  createDiaryEntry: async (projectId, entryData) => {
    if (USE_API) {
      const { data } = await apiClient.post(`/projects/${projectId}/diary`, entryData);
      return data.data || data;
    }

    // Local storage fallback
    throw new Error('Cannot create diary entry without API');
  },

  /**
   * Update a diary entry (within 24 hours only)
   * @param {string|number} projectId - Project ID
   * @param {string|number} entryId - Entry ID
   * @param {Object} updateData - Data to update (description, photoUrls, milestoneId)
   * @returns {Promise<Object>} Updated entry
   */
  updateDiaryEntry: async (projectId, entryId, updateData) => {
    if (USE_API) {
      const { data } = await apiClient.put(`/projects/${projectId}/diary/${entryId}`, updateData);
      return data.data || data;
    }

    // Local storage fallback
    throw new Error('Cannot update diary entry without API');
  },

  /**
   * Delete a diary entry (within 24 hours only)
   * @param {string|number} projectId - Project ID
   * @param {string|number} entryId - Entry ID
   * @returns {Promise<void>}
   */
  deleteDiaryEntry: async (projectId, entryId) => {
    if (USE_API) {
      await apiClient.delete(`/projects/${projectId}/diary/${entryId}`);
      return;
    }

    // Local storage fallback
    throw new Error('Cannot delete diary entry without API');
  },

  /**
   * Upload media (photos and/or videos) for daily diary
   * @param {string|number} projectId - Project ID
   * @param {File[]} files - Array of image/video files
   * @returns {Promise<Array>} Array of uploaded file URLs
   */
  uploadDiaryPhotos: async (projectId, files) => {
    if (!USE_API) {
      throw new Error('Cannot upload media without API');
    }

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('category', 'daily_diary');

    const { data } = await apiClient.post(
      `/projects/${projectId}/files`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000 // 3 min — videos can be up to 100MB
      }
    );

    return data.files || [];
  }
};

export default projectService;
